<script setup lang="ts">
import { defineAsyncComponent, onMounted, Suspense } from "vue";
import { useRouter, useRoute } from "vue-router";
import { locale, theme } from "@/config/antdConfig";
import { getURLSearchParams } from "@/utils";
import { message, App } from "ant-design-vue";
import { storeToRefs } from "pinia";
import { getEventTrackSource } from "@/assets/sensors/event_track_source";
import TopBar from "@/components/Topbar/index.vue";
/**store start */
import { useLoginStore } from "@/store/login";
import { useModelStore } from "@/store/model";
import { useTempRecordStore } from "@/store/tempRecord";
import { useUserStore } from "@/store/user";
/**store end */

/**hooks start */
import { useRetainPopup } from "@/hooks/useRetainPopup";
import { getInviteCodeUserInfo } from "@/hooks/useEventTracking/invite";
import { useEventTracking, getViewName } from "@/hooks/useEventTracking";
import { useUserRightsAutoManager } from "@/hooks/useUserRights";
import { useAscribe, isPromotionLink } from "@/hooks/useAscribe";
import { useGroupExpirePopup } from "@/hooks/useGroupExpirePopup";
import { useMobileUserTipModal } from "@/hooks/useMobileUserTipModal";
import { use618PromotionPopup } from "@/hooks/use618PromotionPopup";
import { useSupportWidget } from "@/hooks/useSupportWidget";
/**hooks end*/

const DownImage = defineAsyncComponent(() => import("@/components/GlobleOthers/DownImage.vue"));
const LoginModal = defineAsyncComponent(() => import("@/components/GlobleModal/LoginModal/index.vue"));
const TeamEditionPurchaseModal = defineAsyncComponent(() => import("@/components/GlobleModal/TeamEditionPurchaseModal/index.vue"));
const ContactServiceModal = defineAsyncComponent(
  () => import("@/components/GlobleModal/TeamEditionPurchaseModal/components/ContactService/index.vue")
);
const ContractApplyModal = defineAsyncComponent(
  () => import("@/components/GlobleModal/TeamEditionPurchaseModal/components/ContractApplyModal/index.vue")
);
const ContractViewModal = defineAsyncComponent(
  () => import("@/components/GlobleModal/TeamEditionPurchaseModal/components/ContractViewModal/index.vue")
);
const UpgradeDisabledModal = defineAsyncComponent(() => import("@/components/GlobleModal/UpgradeDisabledModal/index.vue"));

const SiteMessage = defineAsyncComponent(() => import("@/components/GlobleModal/SiteMessage/index.vue"));
const GlobleModal = defineAsyncComponent(() => import("@/components/GlobleModal/index.vue"));
const SwitchUserTypeModal = defineAsyncComponent(() => import("@/components/GlobleModal/TeamEditionPurchaseModal/SwitchUserTypeModal.vue"));
const ModelTermsNoticeModal = defineAsyncComponent(() => import("@/components/GlobleModal/ModelTermsNoticeModal/index.vue"));

const router = useRouter();
const route = useRoute();
const userStore = useUserStore();
const loginStore = useLoginStore();
const { initModel } = useModelStore();

// 布局组件按需加载映射
const layoutComponents = {
  home: defineAsyncComponent(() => import("@/Layout/template/HomeLayout.vue")),
  homeWorkbench: defineAsyncComponent(() => import("@/Layout/template/HomeWorkbenchLayout.vue")),
  blank: defineAsyncComponent(() => import("@/Layout/template/BlankLayout.vue")),
  simple: defineAsyncComponent(() => import("@/Layout/template/SimpleLayout.vue")),
  create: defineAsyncComponent(() => import("@/Layout/template/CreateLayout.vue")),
  canvas: defineAsyncComponent(() => import("@/Layout/template/CanvasLayout.vue"))
};

// 获取当前布局组件
const getCurrentLayout = (currentRoute: any) => {
  try {
    const layoutName = currentRoute?.meta?.layout as keyof typeof layoutComponents;
    const layoutComponent = layoutComponents[layoutName];

    // 确保返回有效的组件，避免 null 引用
    return layoutComponent || layoutComponents.blank;
  } catch (error) {
    console.warn("获取布局组件时发生错误:", error);
    // 发生错误时返回默认的空白布局
    return layoutComponents.blank;
  }
};

// 提交生图埋点
async function reportLoginPopUpEventTracking() {
  const { login_position, login_type } = loginStore.loginPopUpParams;
  let source = await getEventTrackSource(); //先求默认值
  const eventValue: any = {
    login_position,
    login_type,
    source: source
  };
  try {
    // 邀请码相关埋点字段--如果中途被输入邀请码就需要更改来源信息
    const userInfo = await getInviteCodeUserInfo();
    if (userInfo) {
      const source = await getEventTrackSource("invitation-code");

      eventValue.source = source;
      eventValue.hp_inviter_id = userInfo.id;
      eventValue.hp_inviter_phone_number = userInfo.account;
      eventValue.hp_inviter_code = userInfo.inviteCode;

      delete eventValue.login_position;
    }
  } catch (error) {}

  eventValue.view = getViewName(route.name as string);

  useEventTracking({
    event: "login_pop_up",
    eventValue
  });
}

const { open } = storeToRefs(loginStore);

/**
 * 触发登录弹窗
 * 用于邀请码和小红书等渠道自动弹出登录窗口
 */
function triggerLoginPopup() {
  open.value = true;
  reportLoginPopUpEventTracking();
}

// 邀请链接或小红书投放链接进入,自动弹出登录弹窗
async function autoOpenLoginModal() {
  await router.isReady();
  if (userStore.isLogin) return;

  const inviteCode = getURLSearchParams("inviteCode");
  const shouldTrigger = (inviteCode && !["imageDetails", "shareModel"].includes(route.name as string)) || isPromotionLink();

  if (shouldTrigger) {
    triggerLoginPopup();
  }
}

// 初始化临时记录存储
const tempRecordStore = useTempRecordStore();

onMounted(() => {
  // 初始化临时记录存储（清理过期数据）
  tempRecordStore.initialize();

  // 获取用户类型，提示已成功切换
  const userType = localStorage.getItem("teamEditionUserType");
  if (userType) {
    message.success(`已成功切换至【${userType === "team" ? "团队版" : "个人版"}】`);
    localStorage.removeItem("teamEditionUserType");
  }
  useAscribe();
  autoOpenLoginModal();
  initModel();
});

// 用户权益已通过 useUserRightsAutoManager 自动管理
// 基于 vue-query 提供智能缓存、重试和状态管理

useUserRightsAutoManager();

useRetainPopup();
useGroupExpirePopup();

useMobileUserTipModal();
use618PromotionPopup();

// 初始化 Support Widget（智能客服悬浮球）
useSupportWidget({
  apiBase: import.meta.env.VITE_SUPPORT_API_BASE
});
</script>

<template>
  <div class="app-wrapper">
    <a-config-provider :locale="locale" :theme="theme" class="app-wrapper">
      <App class="app-wrapper">
        <div class="layout-container">
          <a-layout class="layout" :style="{ minWidth: '1024px' }">
            <div class="top-bar-container" v-if="$route.meta.useTopbar">
              <TopBar />
            </div>
            <!-- 动态布局渲染 -->
            <router-view v-slot="{ Component, route }">
              <template v-if="Component">
                <Suspense>
                  <template #default>
                    <component :is="getCurrentLayout(route)" :key="route.meta?.layout || 'blank'" v-if="getCurrentLayout(route)">
                      <component :is="Component" />
                    </component>
                  </template>
                  <template #fallback>
                    <div class="layout-loading">
                      <a-spin size="large" />
                    </div>
                  </template>
                </Suspense>
              </template>
            </router-view>
          </a-layout>
        </div>

        <!-- 登录弹窗 -->
        <LoginModal />
        <!-- 团队版购买弹窗 -->
        <TeamEditionPurchaseModal />
        <!-- 联系客服弹窗 -->
        <ContactServiceModal />
        <!-- 合同申请弹窗 -->
        <ContractApplyModal />
        <!-- 合同查看弹窗 -->
        <ContractViewModal />

        <!-- 禁用升级弹窗 -->
        <UpgradeDisabledModal />

        <!-- 站内信弹窗 -->
        <SiteMessage />

        <!-- 全局下载 -->
        <DownImage />

        <SwitchUserTypeModal />

        <!-- 登录后弹窗 -->
        <GlobleModal />

        <!-- 模型训练条款须知弹窗 -->
        <ModelTermsNoticeModal />

        <!-- 版本检测 -->
        <!-- <VersionCheck /> -->
      </App>
    </a-config-provider>
  </div>
</template>
<style lang="less" scoped>
.app-wrapper {
  width: 100vw;
  height: 100vh;
  height: 100dvh;
}

.layout-container {
  width: 100vw;
  height: 100%;
}

.layout {
  width: 100vw;
  height: 100%;
  overflow: hidden;
  :deep(.ant-layout-header) {
    height: 56px;
    justify-content: space-between;
    align-items: center;
    flex-shrink: 0;
    display: flex;
    padding: 0 32px 0 0;
    background-color: @bgColor;
    border-bottom: 1px solid rgba(235, 235, 245, 0.08);
  }
}

.layout-loading {
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100vw;
  height: 100vh;
  height: 100dvh;
  background-color: @bgColor;
}
</style>
